import {Vector3,Quaternion} from 'three';
import {boardContacts} from './board-motion.js';

const clamp=(v,a=0,b=1)=>Math.max(a,Math.min(b,v));
const smoothstep=(a,b,v)=>{const t=clamp((v-a)/(b-a));return t*t*(3-2*t);};
const finite=(v,fallback=0)=>Number.isFinite(v)?v:fallback;
const UP=new Vector3(0,1,0),BOARD_AXIS=new Vector3(1,0,0);
const CENTER_FRACTION=.42;

// Grounded balance is solved independently of the board's roll. The center
// below is a pelvis/neck mass proxy, not a calibrated physical cat mass model.
// Hands follow the shoulders; no terrain query or hand gap moves the body.
export function createCarveBalance(character){
  character.updateWorldMatrix(true,true);
  const bone=name=>{const b=character.getObjectByName(name);if(!b)throw new Error('Missing balance bone '+name);return b;};
  const pelvis=bone('pelvis'),spine=bone('spine'),head=bone('head');
  const position=b=>b.getWorldPosition(new Vector3());
  const pelvisLength=position(pelvis).distanceTo(position(spine));
  const spineLength=position(spine).distanceTo(position(head));
  const limbs=['L','R'].map((side,index)=>{
    const thigh=bone('thigh'+side),shin=bone('shin'+side),foot=bone('foot'+side);
    const upper=bone('upper_arm'+side),lower=bone('forearm'+side),hand=bone('hand'+side);
    return {side,sign:index===0?1:-1,thigh,shin,foot,upper,lower,hand,
      thighLength:position(thigh).distanceTo(position(shin)),shinLength:position(shin).distanceTo(position(foot)),
      upperLength:position(upper).distanceTo(position(lower)),lowerLength:position(lower).distanceTo(position(hand)),
      anchor:character.worldToLocal(position(foot)),hipOffset:character.worldToLocal(position(thigh)).x-character.worldToLocal(position(pelvis)).x,
      footTarget:new Vector3(),footFrame:new Vector3(),footRotation:new Quaternion()};
  });
  const affected=[pelvis,spine,...limbs.flatMap(l=>[l.thigh,l.shin,l.foot,l.upper,l.lower,l.hand])];
  const authored=affected.map(b=>({bone:b,position:b.position.clone(),quaternion:b.quaternion.clone()}));
  const frameQ=new Quaternion(),frameInverse=new Quaternion(),characterQ=new Quaternion(),origin=new Vector3();
  const rotation=new Quaternion(),worldQ=new Quaternion(),parentQ=new Quaternion(),savedEndQ=new Quaternion();
  const p=new Vector3(),a=new Vector3(),b=new Vector3(),c=new Vector3(),direction=new Vector3(),bend=new Vector3();
  const joint=new Vector3(),start=new Vector3(),end=new Vector3(),target=new Vector3(),pole=new Vector3();
  const support=new Vector3(),hip=new Vector3(),desiredHip=new Vector3(),trackedHip=new Vector3(),neckOffset=new Vector3(),pelvisUp=new Vector3(),torsoUp=new Vector3(),front=new Vector3();
  const shoulder=new Vector3(),naturalHand=new Vector3(),innerHand=new Vector3(),outerHand=new Vector3();
  const state={active:false,weight:0,edge:0,lean:0,depth:0,flexion:0,toeAmount:.5,hipReachCorrection:0,armReachError:0,
    maxAnkleError:0,balanceAngle:0,torsoAngle:0,kneeFlexDegrees:{L:0,R:0},
    hipWorld:new Vector3(),balanceCenterWorld:new Vector3(),supportWorld:new Vector3()};
  let applied=false,weight=0,exitAge=-1,exitWeight=0,trackingHip=false,flexion=0;
  const toFrame=v=>v.sub(origin).applyQuaternion(frameInverse);
  const toWorld=v=>v.applyQuaternion(frameQ).add(origin);
  function restore(){
    if(applied)for(const saved of authored){saved.bone.position.copy(saved.position);saved.bone.quaternion.copy(saved.quaternion);}
    applied=false;
  }
  function aim(bone,desiredAxis,amount=1){
    bone.getWorldQuaternion(worldQ);a.copy(UP).applyQuaternion(worldQ);
    rotation.setFromUnitVectors(a,desiredAxis);rotation.multiply(worldQ);
    worldQ.slerp(rotation,amount);bone.parent.getWorldQuaternion(parentQ).invert();
    bone.quaternion.copy(parentQ.multiply(worldQ));bone.updateWorldMatrix(false,true);
  }
  function solve(upper,lower,tip,wanted,l1,l2,poleDirection){
    upper.getWorldPosition(start);tip.getWorldQuaternion(savedEndQ);
    direction.subVectors(wanted,start);const requested=direction.length();
    const distance=clamp(requested,Math.abs(l1-l2)+.004,l1+l2-.008);
    if(requested<1e-7)return requested;
    direction.multiplyScalar(1/requested);target.copy(start).addScaledVector(direction,distance);
    bend.copy(poleDirection).addScaledVector(direction,-poleDirection.dot(direction));
    if(bend.lengthSq()<1e-8){lower.getWorldPosition(bend);bend.sub(start).addScaledVector(direction,-bend.dot(direction));}
    if(bend.lengthSq()<1e-8)return requested;
    const along=(l1*l1-l2*l2+distance*distance)/(2*distance);
    joint.copy(start).addScaledVector(direction,along).addScaledVector(bend.normalize(),Math.sqrt(Math.max(0,l1*l1-along*along)));
    c.subVectors(joint,start).normalize();aim(upper,c);
    lower.getWorldPosition(end);c.subVectors(target,end).normalize();aim(lower,c);
    tip.parent.getWorldQuaternion(parentQ).invert();tip.quaternion.copy(parentQ.multiply(savedEndQ));tip.updateWorldMatrix(false,true);
    return Math.abs(requested-distance);
  }
  function measure(){
    pelvis.getWorldPosition(state.hipWorld);head.getWorldPosition(state.balanceCenterWorld);
    state.balanceCenterWorld.lerp(state.hipWorld,1-CENTER_FRACTION);
    p.copy(state.balanceCenterWorld).sub(state.supportWorld).applyQuaternion(frameInverse);
    state.balanceAngle=Math.atan2(p.z,p.y);
    head.getWorldPosition(p);p.sub(state.hipWorld).applyQuaternion(frameInverse);state.torsoAngle=Math.atan2(p.z,p.y);
    state.maxAnkleError=0;
    for(const l of limbs){
      l.thigh.getWorldPosition(a);l.shin.getWorldPosition(b);l.foot.getWorldPosition(c);
      state.kneeFlexDegrees[l.side]=(Math.PI-a.sub(b).angleTo(p.subVectors(c,b)))*180/Math.PI;
      state.maxAnkleError=Math.max(state.maxAnkleError,c.distanceTo(l.footTarget));
    }
  }
  return {state,
    restore,
    reset(){
      restore();weight=0;exitAge=-1;exitWeight=0;trackingHip=false;flexion=0;trackedHip.set(0,0,0);
      Object.assign(state,{active:false,weight:0,edge:0,lean:0,depth:0,flexion:0,toeAmount:.5,hipReachCorrection:0,armReachError:0,maxAnkleError:0,balanceAngle:0,torsoAngle:0});
      state.kneeFlexDegrees.L=0;state.kneeFlexDegrees.R=0;
      state.hipWorld.set(0,0,0);state.balanceCenterWorld.set(0,0,0);state.supportWorld.set(0,0,0);
      character.updateWorldMatrix(true,true);return state;
    },
    apply(s,dt,supportWorld){
      state.active=false;state.hipReachCorrection=0;state.armReachError=0;
      const airborne=!s.contact||finite(s.height)>.001||s.phase==='air'||s.phase==='land'||finite(s.weights?.trick)>.01||finite(s.weights?.air)>.01;
      if(airborne){weight=0;exitAge=-1;trackingHip=false;flexion=0;state.weight=0;state.flexion=0;return state;}
      const edge=clamp(finite(s.edge),-1.05,1.05),absoluteEdge=Math.abs(edge);
      const lean=clamp(finite(s.lean),-.95,.95),inclination=Math.max(absoluteEdge,Math.abs(lean));
      const wanted=smoothstep(.025,.16,inclination);
      const step=clamp(finite(dt),0,.1),disengaging=s.phase==='pop'||Math.abs(finite(s.press))>.015||finite(s.pressRelease)>.015||finite(s.charge)>.001;
      if(disengaging){
        if(exitAge<0){exitAge=0;exitWeight=weight;}
        exitAge+=step;weight=exitWeight*(1-smoothstep(0,.24,exitAge));
        // Pop has an authored 0.18 s extension before leaving the snow. Finish
        // the ground modifier within that phase, so it never follows into air.
        if(s.phase==='pop')weight*=1-smoothstep(0,.18,finite(s.phaseAge));
      }else{exitAge=-1;weight+=(wanted-weight)*(1-Math.exp(-14*step));}
      state.weight=weight;state.edge=edge;
      if(weight<.0001){trackingHip=false;flexion=0;state.flexion=0;return state;}
      for(const saved of authored){saved.position.copy(saved.bone.position);saved.quaternion.copy(saved.bone.quaternion);}
      character.updateWorldMatrix(true,true);character.getWorldPosition(origin);character.getWorldQuaternion(characterQ);
      // character inherits Rx(edge). Remove that rotation to obtain the local
      // terrain/yaw/pitch frame in which gravity-side inclination is expressed.
      frameQ.copy(characterQ).multiply(rotation.setFromAxisAngle(BOARD_AXIS,-edge));frameInverse.copy(frameQ).invert();
      if(supportWorld){state.supportWorld.copy(supportWorld);support.copy(supportWorld);toFrame(support);}
      else{
        let minimum=Infinity;support.set(0,0,0);
        for(const contact of boardContacts){p.set(...contact);character.localToWorld(p);toFrame(p);minimum=Math.min(minimum,p.y);}
        let total=0;
        for(const contact of boardContacts){p.set(...contact);character.localToWorld(p);toFrame(p);const w=clamp(1-(p.y-minimum)/.045);support.addScaledVector(p,w);total+=w;}
        support.multiplyScalar(1/Math.max(total,1e-7));state.supportWorld.copy(support);toWorld(state.supportWorld);
      }
      // The center may still be on the old side while the board crosses flat.
      // Keep that motion instead of deleting inclination at edge == 0.
      const toe=smoothstep(-.16,.16,lean),depth=smoothstep(.12,.96,inclination)*(.35+.65*clamp(finite(s.edgeLoad,1)));
      // Release leg flexion progressively while the center crosses the board.
      // Its short load history softens edge changes without delaying real lean
      // or lowering the established steady deep-turn hip targets.
      flexion+=(depth-flexion)*(1-Math.exp(-(depth>flexion?12:6)*step));state.flexion=flexion;
      const torsoAngle=lean*(.56+.25*toe)+(.08-.05*toe)*(1-.35*depth);
      const pelvisAngle=lean+(torsoAngle-lean)*.4;
      state.lean=lean;state.depth=depth;state.toeAmount=toe;
      pelvisUp.set(0,Math.cos(pelvisAngle),Math.sin(pelvisAngle));torsoUp.set(0,Math.cos(torsoAngle),Math.sin(torsoAngle));front.set(0,-torsoUp.z,torsoUp.y);
      neckOffset.copy(pelvisUp).multiplyScalar(pelvisLength).addScaledVector(torsoUp,spineLength);
      pelvis.getWorldPosition(hip);toFrame(hip);
      const height=1.025-(1-toe)*.055-flexion*(.425*toe+.47*(1-toe));
      desiredHip.set(hip.x,support.y+height,support.z+(height+CENTER_FRACTION*neckOffset.y)*Math.tan(lean)-CENTER_FRACTION*neckOffset.z);
      for(const l of limbs){
        l.footTarget.copy(l.anchor);character.localToWorld(l.footTarget);l.footFrame.copy(l.footTarget);toFrame(l.footFrame);
        // Save the authored boot orientation before pelvis motion can rotate
        // the entire leg chain. Binding contact constrains orientation too.
        l.foot.getWorldQuaternion(l.footRotation).normalize();
      }
      if(!trackingHip){trackedHip.copy(hip);trackingHip=true;}
      // Real support can move rapidly across the board during edge release.
      // A short tracking interval keeps the hip path continuous; state exposes
      // the achieved center angle separately from the requested physical lean.
      trackedHip.lerp(desiredHip,1-Math.exp(-18*step));desiredHip.copy(trackedHip);
      // Do not stretch legs to satisfy a center target. Reach limitations are
      // reported, and feet remain fixed at the existing binding anchors.
      for(let pass=0;pass<3;pass++)for(const l of limbs){
        p.copy(desiredHip);p.x+=l.hipOffset;p.sub(l.footFrame);
        const reach=l.thighLength+l.shinLength-.02,distance=p.length();
        if(distance>reach){const correction=distance-reach;desiredHip.addScaledVector(p,-correction/distance);state.hipReachCorrection+=correction;}
      }
      trackedHip.copy(desiredHip);
      hip.lerp(desiredHip,weight);toWorld(hip);pelvis.parent.worldToLocal(hip);pelvis.position.copy(hip);pelvis.updateWorldMatrix(false,true);
      c.copy(pelvisUp).applyQuaternion(frameQ);aim(pelvis,c,weight);
      c.copy(torsoUp).applyQuaternion(frameQ);aim(spine,c,weight);
      for(const l of limbs){
        pole.set(l.sign*.16,0,1).applyQuaternion(characterQ);
        solve(l.thigh,l.shin,l.foot,l.footTarget,l.thighLength,l.shinLength,pole);
        l.foot.parent.getWorldQuaternion(parentQ).invert();l.foot.quaternion.copy(parentQ.multiply(l.footRotation));l.foot.updateWorldMatrix(false,true);
      }
      for(const l of limbs){
        l.upper.getWorldPosition(shoulder);toFrame(shoulder);
        naturalHand.copy(shoulder).addScaledVector(front,.34).addScaledVector(torsoUp,-.64);naturalHand.x+=l.sign*.10;
        // Deep inclination lets the inside elbow relax farther open. The
        // target remains shoulder-relative and short of full arm extension;
        // neither terrain distance nor a contact request determines it.
        innerHand.copy(shoulder);innerHand.x+=l.sign*.12;innerHand.y-=.70+.10*depth;innerHand.z+=(toe*2-1)*.14;
        outerHand.copy(shoulder).addScaledVector(front,.31).addScaledVector(torsoUp,-.30);outerHand.x+=l.sign*.20;
        const inside=l.side==='R'?toe:1-toe;
        outerHand.lerp(innerHand,inside);naturalHand.lerp(outerHand,depth);toWorld(naturalHand);
        l.hand.getWorldPosition(p);naturalHand.lerp(p,1-weight);
        // Match the relaxed authored stance at shallow edges. Only the deep
        // inside elbow opens outward for its established low balancing hand.
        const open=depth*inside;
        pole.copy(front).multiplyScalar(-.13+.31*open).addScaledVector(torsoUp,-.70+.15*open);pole.x+=l.sign*(.15+.20*open);pole.applyQuaternion(frameQ);
        state.armReachError=Math.max(state.armReachError,solve(l.upper,l.lower,l.hand,naturalHand,l.upperLength,l.lowerLength,pole));
      }
      character.updateWorldMatrix(true,true);measure();applied=true;state.active=true;return state;
    }
  };
}
